import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Target, Edit2, X, Check, Crosshair, ArrowLeftRight, ChevronDown, ChevronRight, Play, Square, Trash2, ArrowDownCircle, ArrowUpCircle, AlertTriangle, LayoutGrid, Rows3 } from 'lucide-react';
import { getTheme } from '../App';
import CoinMPanel from '../components/CoinMPanel';
import { useWS } from '../WSContext';
import ManualOrderModal from '../components/ManualOrderModal';
import AssetLogo, { useAssetLogos } from '../components/AssetLogo';
import PairTradeModal from '../components/PairTradeModal';

interface TpForm { type: 'percent' | 'price'; value: string; qtyMode: 'percent' | 'qty'; qtyValue: string; }
interface Props { dark?: boolean; }
interface CloseForm { chunkUsd: string; tpRatio: string; }
interface DripForm { triggerPrice: string; chunkSize: string; chunkPriceType: string; maxTotalQty: string; pauseStrategyId: string; }

function makeId() { return Math.random().toString(36).slice(2, 10); }

const DRIP_PRICE_TYPES = [
  { value: 'best_bid',       label: 'Best Bid' },
  { value: 'best_ask',       label: 'Best Ask' },
  { value: 'best_direction', label: 'Yöne Göre' },
  { value: 'mid',            label: 'Mid Price' },
  { value: 'mark',           label: 'Mark Price' },
  { value: 'oracle',         label: 'Oracle Fiyatı' },
];

export default function Positions({ dark = true }: Props) {
  const { positions: rawPositions, openOrders: rawOpenOrders, account, tickers: rawTickers, connected, sendWs, pairTrades: rawPairTrades, strategies, setStrategies, botList, activeBotId, subscribe, bridge, coinm } = useWS() as any;
  const [tpForms, setTpForms]       = useState<Record<string, TpForm>>({});
  const [tpPopupPos, setTpPopupPos] = useState<any | null>(null);
  const [editingOrder, setEditingOrder] = useState<string | null>(null);
  const [editPrice, setEditPrice]   = useState('');
  const [bestTpPopup, setBestTpPopup] = useState<{ pos: any; price: number; mode: 'percent' | 'qty'; value: string } | null>(null);
  const [showManualOrder, setShowManualOrder] = useState(false);
  const [manualSymbol, setManualSymbol]       = useState<string | undefined>(undefined);
  const [showPairModal, setShowPairModal]     = useState(false);
  const [pairsOpen, setPairsOpen]             = useState(true);
  const [closePopupId, setClosePopupId]       = useState<string | null>(null);
  const [closeForms, setCloseForms]           = useState<Record<string, CloseForm>>({});
  const [dripPopupPos, setDripPopupPos]       = useState<any | null>(null);
  const [dripForm, setDripForm]               = useState<DripForm>({ triggerPrice: '', chunkSize: '', chunkPriceType: 'best_direction', maxTotalQty: '', pauseStrategyId: '' });
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositAmount, setDepositAmount]       = useState('');
  const [depositStatus, setDepositStatus]       = useState<{ msg: string; ok: boolean } | null>(null);
  const [depositLoading, setDepositLoading]     = useState(false);
  const t = getTheme(dark);
  // COIN-M sekmesi yalnız Binance robotunda. Ayrı robot DEĞİL: veri ad-alanı
  // binance1-cm, arayüzde binance1'in altında. USD PnL gösterilmez (bkz. CoinMPanel).
  const isBinance = activeBotId === 'binance1';
  const [pTab, setPTab] = useState<'usd' | 'coinm'>('usd');
  useEffect(() => { if (!isBinance) setPTab('usd'); }, [isBinance]);
  useEffect(() => {
    if (isBinance && pTab === 'coinm') sendWs?.({ type: 'get_coinm_state', payload: {} });
  }, [isBinance, pTab, sendWs]);
  // Varsayılan sekme USD olduğu için açık COIN-M riski gözden kaçmasın:
  // sekme etiketinde açık pozisyon + çalışan grid sayısı, zarardaysa kırmızı nokta.
  const cmOpen = (coinm?.positions?.length || 0) + ((coinm?.grid || []).filter((g: any) => g.running).length);
  const cmLoss = (coinm?.positions || []).some((p: any) => p.unrealizedPnl < 0);

  const activeBot = (botList || []).find((b: any) => b.id === activeBotId);
  const isBluefin = activeBot?.network === 'sui';

  const handleDeposit = useCallback((action: 'deposit_usdc' | 'withdraw_usdc') => {
    const amt = parseFloat(depositAmount);
    if (!amt || amt <= 0) return;
    setDepositLoading(true);
    setDepositStatus(null);
    sendWs({ type: action, payload: { amount: amt } });
  }, [depositAmount, sendWs]);

  // DİNLEYİCİNİN ADI `subscribe`. Burada `addMessageListener` yazıyordu —
  // WSContext böyle bir alan hiç sunmuyor, `as any` cast'i yüzünden TypeScript
  // de yakalamıyordu. Değer çalışma anında undefined kalıyor, efekt ilk satırda
  // dönüyor ve dinleyici HİÇ kaydolmuyordu: yatırma/çekme sonucu (hata DA başarı
  // DA) ekrana hiç ulaşmıyor, buton kalıcı olarak "yükleniyor"da kalıyordu.
  useEffect(() => {
    if (!subscribe) return;
    const unsub = subscribe((msg: any) => {
      if (msg.type === 'deposit_usdc_result' || msg.type === 'withdraw_usdc_result') {
        setDepositLoading(false);
        if (msg.payload?.success) {
          const label = msg.type === 'deposit_usdc_result' ? 'Yatırma' : 'Çekme';
          setDepositStatus({ msg: `${label} başarılı — tx: ${msg.payload?.data?.digest?.slice(0, 16)}...`, ok: true });
          setDepositAmount('');
        } else {
          setDepositStatus({ msg: msg.payload?.error || 'Hata oluştu', ok: false });
        }
      }
    });
    return unsub;
  }, [subscribe]);

  const positions  = rawPositions as any[];

  // ── Görünüm: geniş kart / ızgara ───────────────────────────────────────────
  // Seçim tarayıcıda saklanıyor. Stratejiler sayfasıyla aynı desen, ama ayrı
  // anahtar: iki sayfanın tercihi birbirine bağlı değil.
  const [posLayout, setPosLayout] = useState<'wide' | 'grid'>(() => {
    try { return (localStorage.getItem('bq_positions_layout') as 'wide' | 'grid') || 'wide'; }
    catch { return 'wide'; }
  });
  useEffect(() => {
    try { localStorage.setItem('bq_positions_layout', posLayout); } catch {}
  }, [posLayout]);

  // Logo haritası — yalnız ekrandaki pozisyonların varlıkları isteniyor,
  // sonuç localStorage'da kalıcı (her açılışta yeniden inmesin).
  useAssetLogos(sendWs, subscribe, positions.map((p: any) => p.symbol));

  // NOSYONELE GÖRE SIRALI — her iki düzende de. PnL'e göre sıralamak liste
  // fiyat oynadıkça yerinde durmayacağı için bilerek seçilmedi; nosyonel yalnız
  // dolum/kapanışta değişir, yani sıra sakin kalır ve en çok riskli üstte durur.
  const sortedPositions = useMemo(
    () => [...positions].sort((a, b) =>
      (Math.abs(b.size ?? 0) * (b.markPrice ?? 0)) - (Math.abs(a.size ?? 0) * (a.markPrice ?? 0))),
    [positions],
  );
  const openOrders = rawOpenOrders as any[];
  const pairTrades = (rawPairTrades || []) as any[];
  const tickers    = useMemo(() => {
    const map: Record<string, any> = {};
    (rawTickers as any[]).forEach((tk: any) => { map[tk.symbol] = tk; });
    return map;
  }, [rawTickers]);

  const totalPnl = positions.reduce((s, p) => s + p.pnl, 0);

  const getTpForm = (sym: string): TpForm => tpForms[sym] || { type: 'percent', value: '0.5', qtyMode: 'percent', qtyValue: '100' };
  const setTpField = (sym: string, field: keyof TpForm, val: string) =>
    setTpForms(prev => ({ ...prev, [sym]: { ...getTpForm(sym), [field]: val } }));

  const fmtPrice = (v: number) => v < 1 ? v.toFixed(5) : v.toFixed(4);

  // Funding geri sayımı için saat tiki. 15 saniye yeterli: ekranda dakika
  // çözünürlüğü gösteriliyor, saniye saniye yeniden render etmek bedava değil.
  const [fundingNow, setFundingNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setFundingNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  // İşaretli para — tutar küçük olduğu için 4 hane; 0 ile "bilinmiyor" ayrı.
  const fmtMoney = (v: number) => `${v >= 0 ? '+' : '−'}$${Math.abs(v).toFixed(4)}`;
  const fmtLeft = (ms: number) => {
    const d = ms - fundingNow;
    if (!ms || d <= 0) return null;
    const m = Math.floor(d / 60000);
    return m >= 60 ? `${Math.floor(m / 60)}s ${m % 60}dk` : `${m}dk`;
  };

  const calcTpPrice = (pos: any, form: TpForm): number | null => {
    const tk = tickers[pos.symbol];
    if (form.type === 'percent') {
      const v = parseFloat(form.value);
      return pos.side === 'long' ? pos.entryPrice * (1 + v/100) : pos.entryPrice * (1 - v/100);
    }
    return parseFloat(form.value) || null;
  };

  const calcTpQty = (pos: any, form: TpForm): number => {
    const v = parseFloat(form.qtyValue);
    if (!v || v <= 0) return pos.size;
    return form.qtyMode === 'percent'
      ? Math.min(pos.size * v / 100, pos.size)
      : Math.min(v, pos.size);
  };

  const sendTp = (pos: any) => {
    const form = getTpForm(pos.symbol);
    const price = calcTpPrice(pos, form);
    if (!price) return;
    const quantity = calcTpQty(pos, form);
    if (quantity <= 0) return;
    sendWs({ type: 'manual_tp', payload: { symbol: pos.symbol, side: (pos.side as string).toLowerCase(), price, quantity, leverage: pos.leverage } });
    setTpPopupPos(null);
  };

  // Popup aç — Long → best ask, Short → best bid
  const openBestTpPopup = (pos: any) => {
    const tk = tickers[pos.symbol];
    if (!tk) return;
    const price = pos.side === 'long' ? tk.best_ask : tk.best_bid;
    if (!price) return;
    setBestTpPopup({ pos, price, mode: 'percent', value: '100' });
  };

  const confirmBestTp = () => {
    if (!bestTpPopup) return;
    const { pos, price, mode, value } = bestTpPopup;
    const v = parseFloat(value);
    if (!v || v <= 0) return;
    const qty = mode === 'percent'
      ? Math.min(pos.size * v / 100, pos.size)
      : Math.min(v, pos.size);
    if (qty <= 0) return;
    sendWs({ type: 'manual_tp', payload: { symbol: pos.symbol, side: (pos.side as string).toLowerCase(), price, quantity: qty, leverage: pos.leverage } });
    setBestTpPopup(null);
  };

  const cancelOrder = (order: any) => {
    sendWs({ type: 'cancel_order', payload: { symbol: order.symbol, orderHashes: [order.id || order.hash] } });
  };

  // ── Drip TP yardımcıları ─────────────────────────────────────────────────
  const findDripTp = (symbol: string) =>
    (strategies as any[])?.find(
      (s: any) => s.strategyType === 'tp_only' && s.tpConfig?.mode === 'drip' && s.symbol === symbol
    );

  const openDripPopup = (pos: any) => {
    const existing = findDripTp(pos.symbol);
    const tk = tickers[pos.symbol];
    setDripForm({
      triggerPrice: existing?.tpConfig?.triggerPrice
        ? String(existing.tpConfig.triggerPrice)
        : (tk?.mark_price ? String(tk.mark_price.toFixed(4)) : ''),
      chunkSize:       existing?.tpConfig?.chunkSize ? String(existing.tpConfig.chunkSize) : '',
      chunkPriceType:  existing?.tpConfig?.chunkPriceType ?? 'best_direction',
      maxTotalQty:     existing?.tpConfig?.maxTotalQty ? String(existing.tpConfig.maxTotalQty) : '',
      pauseStrategyId: existing?.tpConfig?.pauseStrategyId ?? '',
    });
    setDripPopupPos(pos);
  };

  const confirmDripTp = () => {
    if (!dripPopupPos) return;
    const pos = dripPopupPos;
    const trigger = parseFloat(dripForm.triggerPrice);
    const chunk   = parseFloat(dripForm.chunkSize);
    if (!trigger || !chunk || trigger <= 0 || chunk <= 0) return;

    const newStrat: any = {
      id:              findDripTp(pos.symbol)?.id ?? makeId(),
      name:            `Drip TP – ${pos.symbol.replace('PERP_','').replace('_USDC','').replace('-PERP','')}`,
      symbol:          pos.symbol,
      side:            pos.side,
      orderSize:       0,
      orderCount:      1,
      leverage:        pos.leverage,
      entryCondition:  'none',
      entryThreshold:  0,
      priceType:       'best_direction',
      active:          true,
      maxPositionSize: 0,
      strategyType:    'tp_only',
      tpConfig: {
        enabled:        true,
        type:           'percent',
        value:          0.5,
        mode:           'drip',
        triggerPrice:   trigger,
        chunkSize:      chunk,
        chunkPriceType: dripForm.chunkPriceType,
        maxTotalQty:     dripForm.maxTotalQty ? parseFloat(dripForm.maxTotalQty) : 0,
        pauseStrategyId: dripForm.pauseStrategyId || undefined,
      },
    };
    setStrategies((prev: any[]) => {
      const filtered = prev.filter(
        (s: any) => !(s.strategyType === 'tp_only' && s.tpConfig?.mode === 'drip' && s.symbol === pos.symbol)
      );
      return [...filtered, newStrat];
    });
    setDripPopupPos(null);
  };

  const stopDripTp = (symbol: string) => {
    setStrategies((prev: any[]) =>
      prev.map((s: any) =>
        s.strategyType === 'tp_only' && s.tpConfig?.mode === 'drip' && s.symbol === symbol
          ? { ...s, active: false }
          : s
      )
    );
  };

  // ── Pair trade yardımcıları ───────────────────────────────────────────────
  const fmtSym = (s: string) => s.replace('PERP_','').replace('_USDC','').replace('-PERP','');

  const getPairPnl = (pair: any) => {
    const legPnl = (leg: any) => {
      if (!leg) return 0;
      const tk = tickers[leg.symbol];
      if (!tk) return 0;
      const rem = leg.total_qty - leg.closed_qty;
      const dir = leg.direction === 'long' ? 1 : -1;
      return (tk.mark_price - leg.entry_price) * rem * dir;
    };
    return legPnl(pair.leg_a) + legPnl(pair.leg_b);
  };

  const getCurrentRatio = (pair: any) => {
    const tkA = tickers[pair.leg_a?.symbol];
    const tkB = tickers[pair.leg_b?.symbol];
    if (!tkA || !tkB || !tkB.mark_price) return null;
    return tkA.mark_price / tkB.mark_price;
  };

  const getCloseForm = (pairId: string): CloseForm =>
    closeForms[pairId] || { chunkUsd: '50', tpRatio: '' };

  const setCloseField = (pairId: string, field: keyof CloseForm, val: string) =>
    setCloseForms(prev => ({ ...prev, [pairId]: { ...getCloseForm(pairId), [field]: val } }));

  const startPairClose = (pair: any) => {
    const form = getCloseForm(pair.id);
    sendWs({
      type: 'start_pair_close',
      payload: {
        pair_id:       pair.id,
        chunk_size_usd: parseFloat(form.chunkUsd) || 50,
        tp_ratio:       parseFloat(form.tpRatio)  || 0,
      },
    });
    setClosePopupId(null);
  };

  const stopPairClose = (pair: any) =>
    sendWs({ type: 'stop_pair_close', payload: { pair_id: pair.id } });

  const deletePair = (pair: any) => {
    if (pair.status === 'closed' || window.confirm(`"${pair.name}" pair trade silinsin mi?`))
      sendWs({ type: 'delete_pair_trade', payload: { pair_id: pair.id } });
  };

  const chunkProgress = (pair: any) => {
    const leg_a = pair.leg_a;
    const leg_b = pair.leg_b;
    if (!leg_a || !leg_b || !leg_a.total_qty) return 0;
    const pctA = leg_a.closed_qty / leg_a.total_qty;
    const pctB = leg_b.closed_qty / leg_b.total_qty;
    return Math.min(((pctA + pctB) / 2) * 100, 100);
  };

  const statusLabel: Record<string, string> = {
    opening: 'Açılıyor', open: 'Açık', closing: 'Kapanıyor', closed: 'Kapalı',
  };
  const statusColor: Record<string, string> = {
    opening: 'text-amber-400', open: 'text-emerald-400',
    closing: 'text-blue-400', closed: 'text-gray-400',
  };

  const inp = `border rounded-lg px-2 py-1.5 text-xs focus:outline-none ${t.inp}`;
  const card = `${t.card} border ${t.cardBorder} rounded-xl`;

  const tabBar = isBinance ? (
    <div className={`inline-flex rounded-lg border ${t.cardBorder} p-0.5 text-xs`}>
      {([['usd', 'USDⓈ-M'], ['coinm', 'COIN-M']] as const).map(([k, label]) => (
        <button key={k} onClick={() => setPTab(k)}
          className={`px-3 py-1.5 rounded-md font-medium flex items-center gap-1.5 transition-colors ${
            pTab === k ? 'bg-[#4A9FE8]/20 text-[#4A9FE8]' : `${t.muted} hover:opacity-80`}`}>
          {label}
          {k === 'coinm' && cmOpen > 0 && (
            <span className="text-[10px] px-1.5 rounded-full bg-amber-500/20 text-amber-300">{cmOpen}</span>
          )}
          {k === 'coinm' && cmLoss && <span className="w-1.5 h-1.5 rounded-full bg-rose-500" title="Zararda COIN-M pozisyonu var" />}
        </button>
      ))}
    </div>
  ) : null;

  if (isBinance && pTab === 'coinm') {
    return (
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className={`text-xl font-bold ${t.text}`}>Pozisyonlar · COIN-M</h2>
          {tabBar}
        </div>
        <CoinMPanel dark={dark} t={t} card={card} coinm={coinm} connected={connected} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <h2 className={`text-xl font-bold ${t.text}`}>Pozisyonlar & Emirler</h2>
          {tabBar}
          {/* KÖPRÜ ROZETİ. Köprü düştüğünde emirler ve kapatmalar sessizce
              geçmiyor — 06/09'da Bluefin LP'de 265 zaman aşımı yaşandı ve
              ekranda hiçbir iz yoktu. Rozet yanında toast bildirimi de var
              (sunucu yalnız durum değişiminde gönderiyor, her hatada değil). */}
          {bridge && bridge.ok === false && (
            <span className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-semibold
                             border border-rose-500/60 bg-rose-500/15 text-rose-300"
              title={`Köprü yanıt vermiyor${bridge.action ? ` (${bridge.action})` : ''}` +
                     `${bridge.error ? ` — ${bridge.error}` : ''}\nEmirler ve kapatmalar geçmeyebilir.`}>
              <AlertTriangle size={12} /> Köprü düştü
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isBluefin && (
            <button onClick={() => { setShowDepositModal(true); setDepositStatus(null); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium border transition-colors border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/20`}>
              <ArrowDownCircle size={13} /> Yatır / Çek
            </button>
          )}
          <button onClick={() => { setManualSymbol(undefined); setShowManualOrder(true); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium border transition-colors border-[#4A9FE8]/60 text-[#4A9FE8] hover:bg-[#4A9FE8]/20`}>
            <Crosshair size={13} /> Manuel Emir
          </button>
          {/* Geniş kart / ızgara anahtarı */}
          <div className={`flex rounded-lg border ${t.cardBorder} overflow-hidden`}>
            {([['wide', Rows3, 'Geniş'], ['grid', LayoutGrid, 'Izgara']] as const).map(([mod, Icon, etiket]) => (
              <button key={mod} onClick={() => setPosLayout(mod)} title={etiket}
                className={`flex items-center gap-1.5 px-2.5 py-2 text-xs transition-colors ${
                  posLayout === mod ? 'bg-[#4A9FE8] text-[#061530] font-bold' : `${t.muted} hover:opacity-80`}`}>
                <Icon size={13} /> {etiket}
              </button>
            ))}
          </div>
          <div className={`flex items-center gap-2 text-xs ${t.muted}`}>
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-rose-400'}`} />
            {connected ? 'Canlı' : 'Bağlantı bekleniyor...'}
          </div>
        </div>
      </div>

      {account && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Equity',         value: `$${(account.totalAccountValue ?? 0).toFixed(2)}` },
            { label: 'Kullanılabilir', value: `$${(account.availableBalance ?? 0).toFixed(2)}` },
            { label: 'USDC Bakiye',    value: `$${(account.usdc_balance ?? 0).toFixed(2)}` },
            { label: 'Unrealized PnL', value: `${(account.crossUnrealizedPnl ?? 0) >= 0 ? '+' : ''}$${(account.crossUnrealizedPnl ?? 0).toFixed(4)}`, pos: (account.crossUnrealizedPnl ?? 0) >= 0 },
          ].map(c => (
            <div key={c.label} className={`${card} p-3`}>
              <p className={`text-xs mb-1 ${t.muted}`}>{c.label}</p>
              <p className={`text-sm font-bold ${(c as any).pos !== undefined ? ((c as any).pos ? t.positive : t.negative) : t.text}`}>{c.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {[
          { label: 'Toplam PnL', value: `${totalPnl >= 0 ? '+' : ''}$${totalPnl.toFixed(4)}`, pos: totalPnl >= 0 },
          { label: 'Açık Pozisyon', value: `${positions.length}` },
        ].map(c => (
          <div key={c.label} className={`${card} p-4`}>
            <p className={`text-xs mb-1 ${t.muted}`}>{c.label}</p>
            <p className={`text-xl font-bold ${(c as any).pos !== undefined ? ((c as any).pos ? t.positive : t.negative) : t.text}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* ── PAİR TRADE BÖLÜMÜ ───────────────────────────────────────────────── */}
      <div className={`${card} overflow-hidden`}>
        {/* Başlık */}
        <div className={`px-4 py-3 flex items-center justify-between border-b ${t.cardBorder} cursor-pointer`}
          onClick={() => setPairsOpen(o => !o)}>
          <div className="flex items-center gap-2">
            {pairsOpen ? <ChevronDown size={14} className={t.muted} /> : <ChevronRight size={14} className={t.muted} />}
            <ArrowLeftRight size={14} className="text-[#4A9FE8]" />
            <span className={`text-sm font-bold ${t.text}`}>Pair Trade'ler</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${t.card} border ${t.cardBorder} ${t.muted}`}>
              {pairTrades.filter(p => p.status !== 'closed').length}
            </span>
          </div>
          <button
            onClick={e => { e.stopPropagation(); setShowPairModal(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-[#4A9FE8]/60 text-[#4A9FE8] hover:bg-[#4A9FE8]/20 transition-colors">
            + Yeni Pair
          </button>
        </div>

        {pairsOpen && (
          <div className="divide-y divide-[#ffffff10]">
            {pairTrades.length === 0 ? (
              <p className={`text-xs text-center py-6 ${t.muted}`}>
                Henüz pair trade yok. "+ Yeni Pair" ile oluşturun.
              </p>
            ) : pairTrades.map(pair => {
              const pnl          = getPairPnl(pair);
              const currentRatio = getCurrentRatio(pair);
              const entryRatio   = pair.entry_ratio || 0;
              const ratioDiff    = currentRatio && entryRatio
                ? ((currentRatio - entryRatio) / entryRatio) * 100 : null;
              const job          = pair.close_job || {};
              const cfg          = pair.close_config || {};
              const progress     = chunkProgress(pair);
              const tpRatio      = parseFloat(cfg.tp_ratio) || 0;
              const ratioOk      = currentRatio !== null && tpRatio > 0 && currentRatio >= tpRatio;

              return (
                <div key={pair.id} className="p-4 space-y-3">
                  {/* Pair başlık satırı */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${t.text}`}>{pair.name}</span>
                      <span className={`text-xs font-medium ${statusColor[pair.status] || 'text-gray-400'}`}>
                        {statusLabel[pair.status] || pair.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${pnl >= 0 ? t.positive : t.negative}`}>
                        {pnl >= 0 ? '+' : ''}${pnl.toFixed(4)}
                      </span>
                      {/* Aksiyonlar */}
                      {pair.status === 'open' && (
                        <button
                          onClick={() => setClosePopupId(pair.id)}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs border border-blue-500/60 text-blue-400 hover:bg-blue-500/20 transition-colors">
                          <Play size={10} /> Kapat
                        </button>
                      )}
                      {pair.status === 'closing' && (
                        <button onClick={() => stopPairClose(pair)}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs border border-amber-500/60 text-amber-400 hover:bg-amber-500/20 transition-colors">
                          <Square size={10} /> Durdur
                        </button>
                      )}
                      <button onClick={() => deletePair(pair)}
                        className={`p-1 rounded ${t.muted} hover:text-rose-400 transition-colors`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Ratio satırı */}
                  <div className={`flex items-center gap-4 text-xs ${t.muted}`}>
                    <span>
                      Giriş Ratio:&nbsp;
                      <span className={`font-mono ${t.text}`}>
                        {entryRatio > 0 ? entryRatio.toFixed(4) : '—'}
                      </span>
                    </span>
                    {currentRatio !== null && (
                      <>
                        <span>Güncel:&nbsp;
                          <span className={`font-mono ${t.text}`}>{currentRatio.toFixed(4)}</span>
                        </span>
                        {ratioDiff !== null && (
                          <span className={ratioDiff >= 0 ? t.positive : t.negative}>
                            {ratioDiff >= 0 ? '▲' : '▼'}{Math.abs(ratioDiff).toFixed(2)}%
                          </span>
                        )}
                        {tpRatio > 0 && (
                          <span>
                            TP Ratio:&nbsp;
                            <span className={`font-mono ${ratioOk ? 'text-emerald-400' : t.text}`}>
                              {tpRatio.toFixed(4)}
                            </span>
                            {ratioOk && <span className="ml-1 text-emerald-400">✓</span>}
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {/* İki leg */}
                  <div className="grid grid-cols-2 gap-2">
                    {[pair.leg_a, pair.leg_b].map((leg: any, i: number) => {
                      if (!leg) return null;
                      const tk     = tickers[leg.symbol];
                      const mark   = tk?.mark_price || 0;
                      const remQty = leg.total_qty - leg.closed_qty;
                      const legPnl = mark
                        ? (mark - leg.entry_price) * remQty * (leg.direction === 'long' ? 1 : -1)
                        : 0;
                      return (
                        <div key={i} className={`rounded-lg p-2.5 border ${t.cardBorder} ${t.card} space-y-1`}>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${
                              leg.direction === 'long'
                                ? 'bg-emerald-900/50 text-emerald-400'
                                : 'bg-rose-900/50 text-rose-400'
                            }`}>
                              {leg.direction.toUpperCase()}
                            </span>
                            <span className={`text-xs font-semibold ${t.text}`}>{fmtSym(leg.symbol)}</span>
                          </div>
                          <p className={`text-xs font-mono ${t.muted}`}>
                            {remQty.toFixed(4)}&nbsp;/&nbsp;{leg.total_qty.toFixed(4)}
                            {leg.closed_qty > 0 && (
                              <span className="ml-1 text-blue-400">
                                ({((leg.closed_qty / leg.total_qty) * 100).toFixed(0)}% kapalı)
                              </span>
                            )}
                          </p>
                          <p className={`text-xs font-mono`}>
                            Giriş:&nbsp;<span className={t.subtext}>${fmtPrice(leg.entry_price)}</span>
                            {mark > 0 && (
                              <span className="ml-2">Mark:&nbsp;
                                <span className={t.subtext}>${fmtPrice(mark)}</span>
                              </span>
                            )}
                          </p>
                          <p className={`text-xs font-bold ${legPnl >= 0 ? t.positive : t.negative}`}>
                            {legPnl >= 0 ? '+' : ''}${legPnl.toFixed(4)}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Kapanış progress (closing durumunda) */}
                  {pair.status === 'closing' && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className={t.muted}>
                          Chunk #{(job.chunk_index || 0) + 1}&nbsp;·&nbsp;
                          {job.state === 'monitoring'
                            ? (job.leg_a_filled && job.leg_b_filled ? 'Tamamlandı' :
                              job.leg_a_filled ? 'Leg A ✓  Leg B ⏳' :
                              job.leg_b_filled ? 'Leg A ⏳  Leg B ✓' :
                              'İkisi de bekleniyor...')
                            : job.state === 'paused'
                            ? `Bekliyor (ratio: ${currentRatio?.toFixed(4)} / ${tpRatio.toFixed(4)})`
                            : 'İşleniyor...'}
                        </span>
                        <span className={`font-mono ${t.text}`}>{progress.toFixed(0)}%</span>
                      </div>
                      <div className={`w-full h-1.5 rounded-full ${t.card} border ${t.cardBorder} overflow-hidden`}>
                        <div
                          className="h-full rounded-full bg-blue-500 transition-all duration-500"
                          style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {positions.length === 0 ? (
        <div className={`${card} p-8 text-center ${t.muted} text-sm`}>
          {connected ? 'Açık pozisyon yok.' : 'Backend bağlantısı bekleniyor...'}
        </div>
      ) : (
        <div className={posLayout === 'grid' ? 'grid grid-cols-4 gap-3' : 'space-y-4'}>
        {sortedPositions.map(p => {

        // ── KOMPAKT KART ───────────────────────────────────────────────────
        // Geniş kartla AYNI veriyi taşır; tek fark yerleşim. Nosyonel sağ üstte,
        // PnL büyük punto (kartta karşılaştırma yerine odak öne çıkıyor),
        // funding tek satırda, dört aksiyon altta eşit genişlikte.
        if (posLayout === 'grid') {
          const drip = findDripTp(p.symbol);
          const sym  = p.symbol.replace('PERP_', '').replace('_USDC', '');
          return (
            <div key={p.symbol} className={`${card} p-3 flex flex-col gap-2`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <AssetLogo symbol={p.symbol} size={20} />
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0 ${
                    p.side === 'long' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'
                  }`}>{p.side.toUpperCase()}</span>
                  <span className={`text-sm font-bold truncate ${t.gold}`} title={sym}>{sym}</span>
                  <span className={`text-[10px] shrink-0 ${t.muted}`}>{p.leverage}x</span>
                </div>
                <span className={`text-[10px] font-mono shrink-0 ${t.text}`}
                  title="Nosyonel — sıralama buna göre">
                  ${(p.size * p.markPrice).toFixed(0)}
                </span>
              </div>

              <div className={`font-mono font-bold text-base leading-none ${p.pnl >= 0 ? t.positive : t.negative}`}>
                {p.pnl >= 0 ? '+' : ''}{p.pnl.toFixed(2)}
                <span className="text-[11px] opacity-70 ml-1">
                  {p.pnlPct >= 0 ? '+' : ''}{p.pnlPct.toFixed(1)}%
                </span>
              </div>

              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px]">
                <div className="truncate">
                  <span className={t.muted}>Miktar </span>
                  <span className={`font-mono ${t.text}`}>{p.size}</span>
                </div>
                <div className="text-right truncate">
                  <span className={t.muted}>Lik. </span>
                  <span className="font-mono text-amber-400">
                    {p.liqPrice ? `$${fmtPrice(p.liqPrice)}` : '—'}
                  </span>
                </div>
                <div className="col-span-2 truncate">
                  <span className={t.muted}>Giriş→Mark </span>
                  <span className={`font-mono ${t.subtext}`}>
                    ${fmtPrice(p.entryPrice)} → ${fmtPrice(p.markPrice)}
                  </span>
                </div>
              </div>

              {/* Funding — geniş karttaki üç bilgi tek satırda: nakit etki,
                  oran/periyot, birikmiş. Bilinmeyen alanlar 0 değil "—". */}
              <div className={`text-[10px] border-t ${t.cardBorder} pt-1.5 flex items-center gap-1.5 flex-wrap`}>
                <span className={t.muted}>Funding</span>
                {p.fundingPerPeriod == null
                  ? <span className={t.muted}>—</span>
                  : <span className={`font-mono ${p.fundingPerPeriod >= 0 ? t.positive : t.negative}`}
                      title={`Günlük ${fmtMoney(p.fundingPerDay || 0)}`}>
                      {fmtMoney(p.fundingPerPeriod)}
                    </span>}
                {p.fundingRate != null && (
                  <span className={`font-mono ${t.muted}`}>
                    {p.fundingRate.toFixed(4)}%/{p.fundingIntervalH || 1}s
                    {fmtLeft(p.fundingNextMs) ? ` · ${fmtLeft(p.fundingNextMs)}` : ''}
                  </span>
                )}
                <span className={`font-mono ${p.fundingSinceOpen == null ? t.muted
                  : (p.fundingSinceOpen >= 0 ? t.positive : t.negative)}`}
                  title={p.fundingSinceOpen == null ? (p.fundingNote || 'Hesaplanamadı')
                    : `${p.fundingPayments} ödeme`}>
                  · birikmiş {p.fundingSinceOpen == null ? '—' : fmtMoney(p.fundingSinceOpen)}
                </span>
              </div>

              <div className="flex gap-1 mt-auto">
                <button onClick={() => openBestTpPopup(p)} disabled={!tickers[p.symbol]}
                  title={`Maker TP @ ${p.side === 'long' ? 'Best Ask' : 'Best Bid'}`}
                  className={`flex-1 px-1 py-1.5 rounded-lg text-[9px] font-medium border transition-colors disabled:opacity-40 ${
                    p.side === 'long'
                      ? 'border-emerald-600 text-emerald-300 hover:bg-emerald-700/40'
                      : 'border-rose-600 text-rose-300 hover:bg-rose-700/40'
                  }`}>
                  {p.side === 'long' ? 'Ask TP' : 'Bid TP'}
                </button>
                <button onClick={() => setTpPopupPos(p)}
                  className={`flex-1 px-1 py-1.5 rounded-lg text-[9px] font-medium ${t.btnSecondary}`}>
                  TP Ekle
                </button>
                {drip?.active ? (
                  <button onClick={() => stopDripTp(p.symbol)}
                    className="flex-1 px-1 py-1.5 rounded-lg text-[9px] font-medium border border-teal-500 bg-teal-500/20 text-teal-300 hover:bg-rose-700/40 hover:border-rose-500 hover:text-rose-300 transition-colors">
                    Drip ●
                  </button>
                ) : (
                  <button onClick={() => openDripPopup(p)}
                    className="flex-1 px-1 py-1.5 rounded-lg text-[9px] font-medium border border-teal-600/60 text-teal-400 hover:bg-teal-600/20 transition-colors">
                    Drip
                  </button>
                )}
                <button onClick={() => { setManualSymbol(p.symbol); setShowManualOrder(true); }}
                  className="flex-1 px-1 py-1.5 rounded-lg text-[9px] font-medium border border-[#4A9FE8]/60 text-[#4A9FE8] hover:bg-[#4A9FE8]/20 transition-colors">
                  Emir
                </button>
              </div>
            </div>
          );
        }
        return (
          <div key={p.symbol} className={`${card} overflow-hidden`}>
            <div className={`px-4 py-3 flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded font-bold ${p.side === 'long' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'}`}>
                  {p.side.toUpperCase()}
                </span>
                <span className={`font-semibold ${t.text}`}>{p.symbol.replace('PERP_','').replace('_USDC','')}</span>
                <span className={`text-xs ${t.muted}`}>{p.leverage}x</span>
              </div>
              {/* flex-wrap: funding 5. hücre olarak eklendi (4 hücre + 4 düğme vardı).
                  Sarma olmadan dar ekranda sağdaki düğmeler kırpılıyordu; yer
                  varken görünüm değişmiyor. */}
              <div className="flex items-center justify-end flex-wrap gap-y-2 gap-5 text-sm">
                <div className="text-right">
                  <p className={`text-xs ${t.muted}`}>Büyüklük</p>
                  <p className={`font-mono ${t.text}`}>{p.size}</p>
                  <p className={`font-mono text-xs ${t.muted}`}>${(p.size * p.markPrice).toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className={`text-xs ${t.muted}`}>Giriş / Mark</p>
                  <p className={`font-mono text-sm ${t.subtext}`}>${fmtPrice(p.entryPrice)} / ${fmtPrice(p.markPrice)}</p>
                  {(() => {
                    const oracle = tickers[p.symbol]?.index_price;
                    return oracle ? (
                      <p className={`font-mono text-[10px] text-blue-400`}>Oracle: ${fmtPrice(oracle)}</p>
                    ) : null;
                  })()}
                </div>
                <div className="text-right">
                  <p className={`text-xs ${t.muted}`}>PnL</p>
                  <p className={`font-bold ${p.pnl >= 0 ? t.positive : t.negative}`}>
                    {p.pnl >= 0 ? '+' : ''}${p.pnl.toFixed(4)} ({p.pnlPct.toFixed(2)}%)
                  </p>
                </div>
                {/* FUNDING — backend'de hesaplanıyor (position_funding.py).
                    Ana satır NAKİT ETKİ: yön × oran × nosyonel, işareti ters
                    çevrilmiş (oran pozitifse long öder). Çıplak oran "kazanıyor
                    muyum" sorusunu cevaplamadığı için tutar öne alındı; oran ve
                    PERİYOT altında duruyor — periyot yazılmazsa 1 saatlik ve 8
                    saatlik borsaların sayıları aynı görünür, 8 kat yanıltır. */}
                <div className="text-right">
                  <p className={`text-xs ${t.muted}`}>Funding</p>
                  {p.fundingPerPeriod == null ? (
                    <p className={`font-mono text-xs ${t.muted}`} title="Bu paritenin funding oranı botun ticker verisinde yok">—</p>
                  ) : (
                    <p className={`font-mono text-sm ${p.fundingPerPeriod >= 0 ? t.positive : t.negative}`}
                      title={`${p.fundingPerPeriod >= 0 ? 'Alıyorsun' : 'Ödüyorsun'} · günlük ${fmtMoney(p.fundingPerDay || 0)}`}>
                      {fmtMoney(p.fundingPerPeriod)}
                    </p>
                  )}
                  <p className={`font-mono text-[10px] ${t.muted}`}>
                    {p.fundingRate == null ? '—' : `${p.fundingRate.toFixed(4)}% / ${p.fundingIntervalH || 1}s`}
                    {fmtLeft(p.fundingNextMs) ? ` · ${fmtLeft(p.fundingNextMs)}` : ''}
                  </p>
                  {p.fundingSinceOpen == null ? (
                    <p className={`text-[10px] ${t.muted} opacity-60`} title={p.fundingNote || 'Hesaplanamadı'}>
                      birikmiş: bilinmiyor
                    </p>
                  ) : (
                    <p className={`font-mono text-[10px] ${p.fundingSinceOpen >= 0 ? t.positive : t.negative} opacity-80`}
                      title={`${p.fundingPayments} ödeme · pozisyon ${p.fundingOpenMs ? new Date(p.fundingOpenMs).toLocaleString('tr-TR') : '—'} tarihinden beri açık${p.fundingNote ? ' · ' + p.fundingNote : ''}`}>
                      birikmiş: {fmtMoney(p.fundingSinceOpen)}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className={`text-xs ${t.muted}`}>Lik. Fiyat</p>
                  <p className={`font-mono text-xs text-amber-400`}>${p.liqPrice?.toFixed(4) || '—'}</p>
                </div>
                <button onClick={() => openBestTpPopup(p)}
                  disabled={!tickers[p.symbol]}
                  title={`Maker TP @ ${p.side === 'long' ? 'Best Ask' : 'Best Bid'}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 ${
                    p.side === 'long'
                      ? 'bg-emerald-700/30 border border-emerald-600 text-emerald-300 hover:bg-emerald-700 hover:text-white'
                      : 'bg-rose-700/30 border border-rose-600 text-rose-300 hover:bg-rose-700 hover:text-white'
                  }`}>
                  <Target size={13} /> {p.side === 'long' ? 'Ask TP' : 'Bid TP'}
                </button>
                <button onClick={() => setTpPopupPos(p)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${t.btnSecondary}`}>
                  <Target size={13} /> TP Ekle
                </button>
                {(() => {
                  const drip = findDripTp(p.symbol);
                  return drip?.active ? (
                    <button onClick={() => stopDripTp(p.symbol)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-teal-500 bg-teal-500/20 text-teal-300 hover:bg-rose-700/40 hover:border-rose-500 hover:text-rose-300 transition-colors">
                      <Crosshair size={13} className="animate-pulse" /> Drip TP ●
                    </button>
                  ) : (
                    <button onClick={() => openDripPopup(p)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-teal-600/60 text-teal-400 hover:bg-teal-600/20 transition-colors">
                      <Crosshair size={13} /> Drip TP
                    </button>
                  );
                })()}
                <button onClick={() => { setManualSymbol(p.symbol); setShowManualOrder(true); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors border-[#4A9FE8]/60 text-[#4A9FE8] hover:bg-[#4A9FE8]/20`}>
                  <Crosshair size={13} /> Emir
                </button>
              </div>
            </div>
          </div>
        );
        })}
        </div>
      )}

      {showManualOrder && (
        <ManualOrderModal dark={dark} defaultSymbol={manualSymbol} onClose={() => setShowManualOrder(false)} />
      )}

      {/* Deposit / Withdraw Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowDepositModal(false)}>
          <div className="bg-[#1a1f2e] border border-white/10 rounded-xl p-6 w-80 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-white mb-4">Bluefin — Yatır / Çek</h3>
            <input
              type="number"
              min="0"
              step="any"
              placeholder="USDC miktar"
              value={depositAmount}
              onChange={e => setDepositAmount(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 mb-4 focus:outline-none focus:border-emerald-500/60"
            />
            <div className="flex gap-2 mb-3">
              <button
                disabled={depositLoading || !depositAmount}
                onClick={() => handleDeposit('deposit_usdc')}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowDownCircle size={13} /> Yatır
              </button>
              <button
                disabled={depositLoading || !depositAmount}
                onClick={() => handleDeposit('withdraw_usdc')}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium bg-rose-500/20 border border-rose-500/40 text-rose-400 hover:bg-rose-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowUpCircle size={13} /> Çek
              </button>
            </div>
            {depositLoading && <p className="text-xs text-center text-white/40">İşlem gönderildi, bekleniyor…</p>}
            {depositStatus && (
              // Hata metni artık API'nin gövdesini de taşıyor (uzun olabilir): sarmalı,
              // seçilebilir ve kaydırılabilir olsun — dar modalda tek satır taşıyordu.
              <p className={`text-xs mt-1 break-words whitespace-pre-wrap select-text
                max-h-32 overflow-y-auto ${depositStatus.ok
                  ? 'text-emerald-400 text-center' : 'text-rose-400 text-left'}`}>
                {depositStatus.msg}
              </p>
            )}
          </div>
        </div>
      )}

      {showPairModal && (
        <PairTradeModal dark={dark} onClose={() => setShowPairModal(false)} />
      )}

      {/* Pair Kapatma Popup */}
      {closePopupId && (() => {
        const pair = pairTrades.find((p: any) => p.id === closePopupId);
        if (!pair) return null;
        const form = getCloseForm(pair.id);
        const currentRatio = getCurrentRatio(pair);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
            onClick={() => setClosePopupId(null)}>
            <div className={`${card} p-5 w-80 space-y-4`} onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h3 className={`font-bold text-sm ${t.text}`}>Pair Kapatma — {pair.name}</h3>
                <button onClick={() => setClosePopupId(null)} className={`${t.muted} hover:text-rose-400`}>
                  <X size={15} />
                </button>
              </div>
              {currentRatio !== null && (
                <p className={`text-xs ${t.muted}`}>
                  Güncel Ratio:&nbsp;
                  <span className={`font-mono font-bold ${t.text}`}>{currentRatio.toFixed(6)}</span>
                </p>
              )}
              <div>
                <label className={`text-xs block mb-1 ${t.muted}`}>Chunk Büyüklüğü (USDC)</label>
                <input type="number" min="1" step="10" className={`${inp} w-full`}
                  value={form.chunkUsd}
                  onChange={e => setCloseField(pair.id, 'chunkUsd', e.target.value)} />
              </div>
              <div>
                <label className={`text-xs block mb-1 ${t.muted}`}>
                  TP Ratio eşiği&nbsp;
                  <span className="opacity-60">(bu değere ulaşınca kapanır)</span>
                </label>
                <input type="number" step="any" className={`${inp} w-full`}
                  placeholder={currentRatio ? currentRatio.toFixed(6) : '0'}
                  value={form.tpRatio}
                  onChange={e => setCloseField(pair.id, 'tpRatio', e.target.value)} />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => startPairClose(pair)}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs py-2 rounded-lg">
                  Kapatmayı Başlat
                </button>
                <button onClick={() => setClosePopupId(null)}
                  className={`flex-1 text-xs py-2 rounded-lg ${t.btnSecondary}`}>
                  İptal
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* TP Ekle Popup */}
      {tpPopupPos && (() => {
        const p = tpPopupPos;
        const form = getTpForm(p.symbol);
        const tpPrice = calcTpPrice(p, form);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setTpPopupPos(null)}>
            <div className={`${card} p-5 w-80 space-y-4`} onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h3 className={`font-bold text-sm ${t.text}`}>
                  TP Ekle — {p.symbol.replace('PERP_','').replace('_USDC','')}
                </h3>
                <button onClick={() => setTpPopupPos(null)} className={`${t.muted} hover:text-rose-400`}><X size={15}/></button>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setTpField(p.symbol, 'type', 'percent')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.type === 'percent' ? 'bg-[#4A9FE8] text-[#061530] border-[#4A9FE8]' : t.btnSecondary}`}>
                  Yüzde (%)
                </button>
                <button onClick={() => setTpField(p.symbol, 'type', 'price')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.type === 'price' ? 'bg-[#4A9FE8] text-[#061530] border-[#4A9FE8]' : t.btnSecondary}`}>
                  Sabit Fiyat ($)
                </button>
              </div>
              <div>
                <label className={`text-xs block mb-1 ${t.muted}`}>{form.type === 'percent' ? 'Yüzde (%)' : 'Fiyat ($)'}</label>
                <input type="number" step="0.0001" autoFocus
                  className={`${inp} w-full`}
                  value={form.value}
                  onChange={e => setTpField(p.symbol, 'value', e.target.value)} />
              </div>
              <div className={`text-xs ${t.muted}`}>
                TP Fiyatı:{' '}
                <span className="font-mono font-bold text-emerald-400">
                  {tpPrice ? `$${fmtPrice(tpPrice)}` : '—'}
                </span>
              </div>
              <div className={`border-t ${t.cardBorder} pt-3 space-y-2`}>
                <div className="flex gap-2">
                  <button onClick={() => setTpField(p.symbol, 'qtyMode', 'percent')}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.qtyMode === 'percent' ? 'bg-[#4A9FE8] text-[#061530] border-[#4A9FE8]' : t.btnSecondary}`}>
                    Yüzde (%)
                  </button>
                  <button onClick={() => setTpField(p.symbol, 'qtyMode', 'qty')}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.qtyMode === 'qty' ? 'bg-[#4A9FE8] text-[#061530] border-[#4A9FE8]' : t.btnSecondary}`}>
                    Adet
                  </button>
                </div>
                <input type="number" min="0" step={form.qtyMode === 'percent' ? '1' : '0.001'}
                  className={`${inp} w-full`}
                  value={form.qtyValue}
                  onChange={e => setTpField(p.symbol, 'qtyValue', e.target.value)} />
                <p className={`text-xs ${t.muted}`}>
                  Miktar:{' '}
                  <span className={`font-mono font-bold ${t.text}`}>
                    {calcTpQty(p, form).toFixed(4)}
                  </span>
                  <span className="opacity-50"> / {p.size}</span>
                </p>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => sendTp(p)} disabled={!tpPrice}
                  className="flex-1 bg-[#4A9FE8] hover:bg-[#e5a820] disabled:opacity-40 text-[#061530] font-bold text-xs py-2 rounded-lg">
                  Gönder
                </button>
                <button onClick={() => setTpPopupPos(null)}
                  className={`flex-1 text-xs py-2 rounded-lg ${t.btnSecondary}`}>
                  İptal
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Best Maker TP Popup */}
      {bestTpPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setBestTpPopup(null)}>
          <div className={`${card} p-5 w-80 space-y-4`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className={`font-bold text-sm ${t.text}`}>
                {bestTpPopup.pos.side === 'long' ? 'Ask TP' : 'Bid TP'} — {bestTpPopup.pos.symbol.replace('PERP_','').replace('_USDC','')}
              </h3>
              <button onClick={() => setBestTpPopup(null)} className={`${t.muted} hover:text-rose-400`}><X size={15}/></button>
            </div>
            <div className={`text-xs ${t.muted}`}>
              Fiyat: <span className={`font-mono font-bold ${t.text}`}>${bestTpPopup.price.toFixed(4)}</span>
              &nbsp;·&nbsp;Pozisyon: <span className={`font-mono font-bold ${t.text}`}>{bestTpPopup.pos.size}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setBestTpPopup(prev => prev ? { ...prev, mode: 'percent' } : prev)}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${bestTpPopup.mode === 'percent' ? 'bg-[#4A9FE8] text-[#061530] border-[#4A9FE8]' : `${t.btnSecondary}`}`}>
                Yüzde (%)
              </button>
              <button
                onClick={() => setBestTpPopup(prev => prev ? { ...prev, mode: 'qty' } : prev)}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${bestTpPopup.mode === 'qty' ? 'bg-[#4A9FE8] text-[#061530] border-[#4A9FE8]' : `${t.btnSecondary}`}`}>
                Adet
              </button>
            </div>
            <div>
              <label className={`text-xs block mb-1 ${t.muted}`}>
                {bestTpPopup.mode === 'percent' ? 'Yüzde (%)' : 'Adet'}
              </label>
              <input
                type="number" min="0" step={bestTpPopup.mode === 'percent' ? '1' : '0.001'}
                className={`${inp} w-full`}
                value={bestTpPopup.value}
                onChange={e => setBestTpPopup(prev => prev ? { ...prev, value: e.target.value } : prev)}
                autoFocus
              />
            </div>
            <div className={`text-xs ${t.muted}`}>
              Satılacak miktar:{' '}
              <span className={`font-mono font-bold ${t.text}`}>
                {(() => {
                  const v = parseFloat(bestTpPopup.value);
                  if (!v || v <= 0) return '—';
                  const qty = bestTpPopup.mode === 'percent'
                    ? Math.min(bestTpPopup.pos.size * v / 100, bestTpPopup.pos.size)
                    : Math.min(v, bestTpPopup.pos.size);
                  return qty.toFixed(4);
                })()}
              </span>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={confirmBestTp}
                className="flex-1 bg-[#4A9FE8] hover:bg-[#e5a820] text-[#061530] font-bold text-xs py-2 rounded-lg">
                Gönder
              </button>
              <button onClick={() => setBestTpPopup(null)}
                className={`flex-1 text-xs py-2 rounded-lg ${t.btnSecondary}`}>
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drip TP Popup */}
      {dripPopupPos && (() => {
        const pos = dripPopupPos;
        const tk  = tickers[pos.symbol];
        const sym = pos.symbol.replace('PERP_','').replace('_USDC','').replace('-PERP','');
        const trigger = parseFloat(dripForm.triggerPrice);
        const chunk   = parseFloat(dripForm.chunkSize);
        const canConfirm = trigger > 0 && chunk > 0;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
            onClick={() => setDripPopupPos(null)}>
            <div className={`${card} p-5 w-80 space-y-4`} onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Crosshair size={14} className="text-teal-400" />
                  <h3 className={`font-bold text-sm ${t.text}`}>Drip TP — {sym}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                    pos.side === 'long' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'
                  }`}>{pos.side.toUpperCase()}</span>
                  <span className={`text-xs ${t.muted}`}>{pos.leverage}x</span>
                </div>
                <button onClick={() => setDripPopupPos(null)} className={`${t.muted} hover:text-rose-400`}>
                  <X size={15} />
                </button>
              </div>

              {tk?.mark_price && (
                <p className={`text-xs ${t.muted}`}>
                  Mark: <span className={`font-mono font-bold ${t.text}`}>${tk.mark_price.toFixed(4)}</span>
                  &nbsp;·&nbsp;
                  {pos.side === 'long' ? 'Fiyat ≥ trigger olduğunda kapatar' : 'Fiyat ≤ trigger olduğunda kapatar'}
                </p>
              )}

              <div>
                <label className={`text-xs block mb-1 ${t.muted}`}>
                  Tetik Fiyatı ($)&nbsp;
                  <span className={pos.side === 'long' ? 'text-emerald-400' : 'text-rose-400'}>
                    {pos.side === 'long' ? '≥' : '≤'}
                  </span>
                </label>
                <input type="number" step="0.01" min="0" autoFocus
                  className={`${inp} w-full`}
                  value={dripForm.triggerPrice}
                  onChange={e => setDripForm(f => ({ ...f, triggerPrice: e.target.value }))} />
              </div>

              <div>
                <label className={`text-xs block mb-1 ${t.muted}`}>Parça Büyüklüğü (adet)</label>
                <input type="number" step="0.01" min="0"
                  className={`${inp} w-full`}
                  value={dripForm.chunkSize}
                  onChange={e => setDripForm(f => ({ ...f, chunkSize: e.target.value }))} />
              </div>

              <div>
                <label className={`text-xs block mb-1 ${t.muted}`}>Fiyat Tipi</label>
                <select className={`${inp} w-full`}
                  value={dripForm.chunkPriceType}
                  onChange={e => setDripForm(f => ({ ...f, chunkPriceType: e.target.value }))}>
                  {DRIP_PRICE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                </select>
              </div>

              <div>
                <label className={`text-xs block mb-1 ${t.muted}`}>Maks Kapatılacak Miktar <span className={t.muted}>(boş = sınırsız)</span></label>
                <input type="number" step="0.01" min="0"
                  className={`${inp} w-full`}
                  placeholder="0"
                  value={dripForm.maxTotalQty}
                  onChange={e => setDripForm(f => ({ ...f, maxTotalQty: e.target.value }))} />
              </div>

              {/* Tetiklenince kaynak stratejiyi durdur */}
              {(() => {
                const pos = dripPopupPos;
                const sourceStrats = (strategies as any[]).filter(
                  (s: any) => s.symbol === pos?.symbol
                    && s.side === pos?.side
                    && s.strategyType !== 'tp_only'
                    && s.active
                );
                if (sourceStrats.length === 0) return null;
                return (
                  <div>
                    <label className={`text-xs block mb-1 ${t.muted}`}>
                      Tetiklenince durdur
                      <span className={`ml-1 opacity-60`}>(isteğe bağlı)</span>
                    </label>
                    <select className={`${inp} w-full`}
                      value={dripForm.pauseStrategyId}
                      onChange={e => setDripForm(f => ({ ...f, pauseStrategyId: e.target.value }))}>
                      <option value="">— seçme —</option>
                      {sourceStrats.map((s: any) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({(strategies as any[]).findIndex((x: any) => x.id === s.id) + 1}. strateji)
                        </option>
                      ))}
                    </select>
                    {dripForm.pauseStrategyId && (
                      <p className="text-[10px] text-amber-400 mt-1">
                        ⚠ Drip TP tetiklendiğinde seçili strateji otomatik durdurulur.
                      </p>
                    )}
                  </div>
                );
              })()}

              <div className="flex gap-2 pt-1">
                <button onClick={confirmDripTp} disabled={!canConfirm}
                  className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:opacity-40 text-white font-bold text-xs py-2 rounded-lg">
                  Başlat
                </button>
                <button onClick={() => setDripPopupPos(null)}
                  className={`flex-1 text-xs py-2 rounded-lg ${t.btnSecondary}`}>
                  İptal
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Açık Emirler */}
      <div className={`${card} overflow-hidden`}>
        <div className={`px-4 py-3 border-b ${t.cardBorder} flex items-center justify-between`}>
          <h3 className={`text-sm font-semibold ${t.text}`}>Açık Emirler</h3>
          <span className={`text-xs ${t.muted}`}>{openOrders.length} emir</span>
        </div>
        {openOrders.length === 0 ? (
          <div className={`p-8 text-center ${t.muted} text-xs`}>Açık emir yok.</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className={`${t.tableHeader} border-b`}>
                {['Sembol','Yön','Tip','Fiyat','Miktar','Reduce Only','İşlem'].map((h, i) => (
                  <th key={h} className={`px-4 py-2.5 font-medium ${i < 2 ? 'text-left' : 'text-right'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {openOrders.map((o, idx) => {
                const hash = o.id || o.hash || String(idx);
                const isEditing = editingOrder === hash;
                return (
                  <tr key={hash} className={`border-b ${t.tableRow}`}>
                    <td className={`px-4 py-2.5 font-semibold ${t.gold}`}>{o.symbol.replace('PERP_','').replace('_USDC','')}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        o.side === 'BUY' || o.side === 'LONG' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'
                      }`}>{o.side}</span>
                    </td>
                    <td className={`px-4 py-2.5 text-right ${t.muted}`}>{o.type || 'LIMIT'}</td>
                    <td className={`px-4 py-2.5 text-right font-mono ${t.text}`}>
                      {isEditing
                        ? <input type="number" className={`${inp} w-28`} value={editPrice} onChange={e => setEditPrice(e.target.value)} />
                        : `$${fmtPrice(parseFloat(o.price || '0'))}`}
                    </td>
                    <td className={`px-4 py-2.5 text-right font-mono ${t.text}`}>
                      {parseFloat(o.filledQty || '0') > 0
                        ? <><span className="text-emerald-400">{o.filledQty}</span>/{o.quantity}</>
                        : o.quantity}
                    </td>
                    <td className={`px-4 py-2.5 text-right ${t.muted}`}>
                      {o.reduceOnly ? <span className="text-amber-400">TP/Kapat</span> : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {isEditing ? (
                          <>
                            <button onClick={() => {
                              const newPrice = parseFloat(editPrice);
                              if (newPrice > 0) {
                                sendWs({ type: 'modify_order', payload: {
                                  orderHash: hash,
                                  symbol: o.symbol,
                                  side: o.side,
                                  newPrice,
                                  newQuantity: parseFloat(o.quantity || '0'),
                                  reduceOnly: o.reduceOnly ?? false,
                                }});
                              }
                              setEditingOrder(null);
                            }} className="p-1 text-emerald-400"><Check size={14}/></button>
                            <button onClick={() => setEditingOrder(null)} className={`p-1 ${t.muted}`}><X size={14}/></button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => { setEditingOrder(hash); setEditPrice(o.price || ''); }}
                              className={`p-1 ${t.muted} hover:text-[#4A9FE8]`}><Edit2 size={14}/></button>
                            <button onClick={() => cancelOrder(o)} className="p-1 text-rose-400 hover:text-rose-300"><X size={14}/></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
